import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation, useStaggerAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

const services = [
  {
    icon: "ri-home-heart-line",
    title: "身体介護",
    subtitle: "Physical Care",
    desc: "入浴・排泄・食事などの介助を行います。利用者様の尊厳を大切にしながら、安全で快適な日常生活をサポートします。",
    items: ["入浴介助", "排泄介助", "食事介助", "体位変換", "移乗・移動介助", "服薬介助"],
  },
  {
    icon: "ri-restaurant-line",
    title: "生活援助",
    subtitle: "Daily Life Support",
    desc: "掃除・洗濯・調理など、日常生活に必要な家事全般をサポートします。住み慣れた自宅での生活を継続できるよう支援します。",
    items: ["掃除・整理整頓", "洗濯・衣類の整理", "調理・配膳", "買い物代行", "薬の受け取り", "ゴミ出し"],
  },
  {
    icon: "ri-hospital-line",
    title: "通院等乗降介助",
    subtitle: "Transportation Support",
    desc: "病院や施設への通院・外出時の乗降介助を行います。安心して外出できるよう、丁寧にサポートします。",
    items: ["通院時の乗降介助", "外出時の付き添い", "車いす対応", "院内での介助"],
  },
  {
    icon: "ri-heart-pulse-line",
    title: "障害福祉サービス（居宅介護）",
    subtitle: "Disability Welfare Service",
    desc: "障害のある方が自宅で安心して生活できるよう、身体介護・家事援助・通院等介助を提供します。",
    items: ["身体介護", "家事援助", "通院等介助", "重度訪問介護", "行動援護"],
  },
];

const flow = [
  { step: "01", title: "お問い合わせ", desc: "まずはお電話またはお問い合わせフォームよりご連絡ください。" },
  { step: "02", title: "ご相談・アセスメント", desc: "担当者がご自宅を訪問し、現在の状況やご要望をお伺いします。" },
  { step: "03", title: "ケアプランの作成", desc: "ケアマネジャーと連携し、最適なサービス計画を作成します。" },
  { step: "04", title: "サービス開始", desc: "ご納得いただいた上でサービスを開始します。定期的に状況を確認しながら継続的にサポートします。" },
];

export default function ServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.15 });
  const { ref: servicesTitleRef, isVisible: servicesTitleVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, visibleItems: cardItems } = useStaggerAnimation(services.length, { threshold: 0.1 });
  const { ref: flowTitleRef, isVisible: flowTitleVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: flowRef, visibleItems: flowItems } = useStaggerAnimation(flow.length, { threshold: 0.1 });
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation({ threshold: 0.15 });

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[340px] md:h-[420px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/88f906dc9dfc6387b6f9520153b6f935.png"
          alt="株式会社FD 事業案内 訪問介護・障害福祉サービス"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div
          className="absolute inset-0 flex items-center justify-center"
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="text-center">
            <p
              className="text-white/70 text-xs font-['Poppins'] tracking-widest mb-3"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(20px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              SERVICE
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold text-white tracking-wide"
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s",
              }}
            >
              事業案内
            </h1>
            <div
              className="flex items-center justify-center gap-2 mt-4 text-xs text-white/60"
              style={{
                opacity: heroVisible ? 1 : 0,
                transition: "opacity 0.8s ease 0.3s",
              }}
            >
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">TOP</Link>
              <span>›</span>
              <span className="text-white/90">事業案内</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white" ref={introRef as React.RefObject<HTMLElement>}>
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-14">
            <div
              className="flex-1 h-72 md:h-96 rounded-2xl overflow-hidden"
              style={{
                opacity: introVisible ? 1 : 0,
                transform: introVisible ? "translateX(0)" : "translateX(-40px)",
                transition: "opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s",
              }}
            >
              <img
                src="https://static.readdy.ai/image/e643b173cc54a8b3f926f18f1e409a57/7ff0d11f22fecd318d5e10f42fca0738.jpeg"
                alt="みりんのサービス"
                className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="flex-1">
              <p
                className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-3"
                style={{
                  opacity: introVisible ? 1 : 0,
                  transform: introVisible ? "translateX(0)" : "translateX(20px)",
                  transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
                }}
              >
                HELPER STATION MIRIN
              </p>
              <h2
                className="text-2xl md:text-3xl font-bold text-gray-800 leading-snug mb-6"
                style={{
                  opacity: introVisible ? 1 : 0,
                  transform: introVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
                }}
              >
                ヘルパーステーションみりん
              </h2>
              <div
                className="space-y-4 text-sm text-gray-600 leading-relaxed"
                style={{
                  opacity: introVisible ? 1 : 0,
                  transform: introVisible ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s",
                }}
              >
                <p>
                  ケアマネジャー・相談支援専門員などと連携をとりながら、ご本人・ご家族の不安を少しでも取り除き、安心してご自宅で介護・生活支援を受けられるようサポートします。
                </p>
                <p>
                  訪問介護・障害福祉サービスを通じて、幅広いニーズに応じた最適なサービスをご提供します。一人ひとりの状況に合わせた丁寧なケアを心がけています。
                </p>
                <div className="pt-2 space-y-1 text-xs text-gray-500">
                  <p>〒448-0001 愛知県刈谷市井ケ谷町下前田6番地3 コーポ龍美218号室</p>
                  <p>TEL: <a href="tel:0566-68-6220" className="text-[#3ab5c6] hover:underline cursor-pointer">0566-68-6220</a>　/　FAX: 0566-68-6221</p>
                  <p>介護指定番号: 2372902144　/　障害指定番号: 2313300887</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-[#f0fafb]">
        <div className="max-w-[1240px] mx-auto px-6">
          <div
            className="mb-12 text-center"
            ref={servicesTitleRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: servicesTitleVisible ? 1 : 0,
              transform: servicesTitleVisible ? "translateY(0)" : "translateY(25px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            <p className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-2">OUR SERVICES</p>
            <h2 className="text-2xl font-bold text-gray-800">提供サービス一覧</h2>
          </div>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            ref={cardsRef as React.RefObject<HTMLDivElement>}
          >
            {services.map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8"
                style={{
                  opacity: cardItems[i] ? 1 : 0,
                  transform: cardItems[i] ? "translateY(0)" : "translateY(35px)",
                  transition: "opacity 0.7s ease, transform 0.7s ease",
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f8fa] mb-5">
                  <i className={`${s.icon} text-[#3ab5c6] text-xl`}></i>
                </div>
                <p className="text-[#3ab5c6] text-[10px] font-['Poppins'] tracking-widest mb-1">{s.subtitle}</p>
                <h3 className="text-lg font-bold text-gray-800 mb-3">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">{s.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {s.items.map((item) => (
                    <span key={item} className="text-xs border border-[#3ab5c6] text-[#3ab5c6] px-2.5 py-1 rounded-full whitespace-nowrap">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="py-20 bg-white">
        <div className="relative w-full">
          {/* コンテンツ本体 */}
          <div className="max-w-[640px] mx-auto px-6">
            <div
              className="mb-12 text-center"
              ref={flowTitleRef as React.RefObject<HTMLDivElement>}
              style={{
                opacity: flowTitleVisible ? 1 : 0,
                transform: flowTitleVisible ? "translateY(0)" : "translateY(25px)",
                transition: "opacity 0.7s ease, transform 0.7s ease",
              }}
            >
              <p className="text-[#3ab5c6] text-xs font-['Poppins'] tracking-widest mb-2">HOW TO START</p>
              <h2 className="text-2xl font-bold text-gray-800">ご利用の流れ</h2>
            </div>
            <div
              className="space-y-6"
              ref={flowRef as React.RefObject<HTMLDivElement>}
            >
              {flow.map((f, i) => (
                <div
                  key={i}
                  className="flex items-start gap-6"
                  style={{
                    opacity: flowItems[i] ? 1 : 0,
                    transform: flowItems[i] ? "translateX(0)" : "translateX(-30px)",
                    transition: "opacity 0.7s ease, transform 0.7s ease",
                  }}
                >
                  <div className="shrink-0 w-14 h-14 flex items-center justify-center rounded-full bg-[#3ab5c6] text-white font-bold font-['Poppins'] text-sm">
                    {f.step}
                  </div>
                  <div className="flex-1 pt-3">
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{f.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* マスコットキャラクター — ボックス右の空白に絶対配置 */}
          <div
            className="hidden xl:block absolute top-1/3 -translate-y-1/3 w-72 h-72"
            style={{
              left: "calc(50% + 360px)",
              animation: flowTitleVisible ? "flowMascot 4s ease-in-out infinite" : "none",
              opacity: flowTitleVisible ? 1 : 0,
              transition: "opacity 0.9s ease 0.5s",
            }}
          >
            <img
              src="https://storage.readdy-site.link/project_files/26e3b86f-cc28-43d6-ae72-d910dd967b3f/74664114-7eb6-4d4b-bb03-de707c7dad0c_unnamed.png?v=bdd747330502976547040eb0564db7e4"
              alt="ヘルパーステーションみりん マスコットキャラクター"
              className="w-full h-full object-contain"
            />
          </div>
        </div>
        <style>{`
          @keyframes flowMascot {
            0%, 100% { transform: translateY(0) rotate(5deg); }
            50% { transform: translateY(-12px) rotate(5deg); }
          }
        `}</style>
      </section>

      {/* CTA */}
      <section
        className="py-16 bg-[#3ab5c6]"
        ref={infoRef as React.RefObject<HTMLElement>}
      >
        <div
          className="max-w-[900px] mx-auto px-6 text-center"
          style={{
            opacity: infoVisible ? 1 : 0,
            transform: infoVisible ? "translateY(0)" : "translateY(25px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <p className="text-white/80 text-xs font-['Poppins'] tracking-widest mb-3">CONTACT</p>
          <h2 className="text-2xl font-bold text-white mb-4">まずはお気軽にご相談ください</h2>
          <p className="text-white/90 text-sm mb-8">
            サービスに関するご質問・ご相談はお電話またはお問い合わせフォームよりお気軽にどうぞ。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:0566-68-6220"
              className="flex items-center gap-2 bg-white text-[#3ab5c6] font-bold text-sm px-8 py-3 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              0566-68-6220
            </a>
            <Link
              to="/#contact"
              className="flex items-center gap-2 border border-white text-white text-sm px-8 py-3 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              お問い合わせフォームへ
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Schema.org Service Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "訪問介護・障害福祉サービス｜株式会社FD",
          "description": "愛知県刈谷市のヘルパーステーションみりんが提供する訪問介護・障害福祉サービス。身体介護・生活援助・通院等乗降介助・居宅介護・重度訪問介護をご提供します。",
          "provider": {
            "@type": "Organization",
            "name": "株式会社FD",
            "url": "https://future-diver.jp/"
          },
          "areaServed": {
            "@type": "City",
            "name": "刈谷市"
          },
          "serviceType": ["訪問介護", "障害福祉サービス", "居宅介護", "重度訪問介護"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "提供サービス",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "身体介護",
                  "description": "入浴・排泄・食事などの介助を行います。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "生活援助",
                  "description": "掃除・洗濯・調理などの家事全般をサポートします。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "通院等乗降介助",
                  "description": "病院や施設への通院・外出時の乗降介助を行います。"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "障害福祉サービス（居宅介護）",
                  "description": "障害のある方が自宅で安心して生活できるよう支援します。"
                }
              }
            ]
          }
        })}
      </script>

      {/* Schema.org FAQPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "訪問介護サービスの対応エリアはどこですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "愛知県刈谷市およびその周辺地域を対応エリアとしております。詳細はお電話（0566-68-6220）にてお問い合わせください。"
              }
            },
            {
              "@type": "Question",
              "name": "障害福祉サービスとは何ですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "障害のある方が自宅で安心して生活できるよう、身体介護・家事援助・通院等介助を提供するサービスです。居宅介護・重度訪問介護が該当します。"
              }
            },
            {
              "@type": "Question",
              "name": "サービスの利用にはどのような手続きが必要ですか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "まずお電話またはお問い合わせフォームよりご連絡ください。担当者がご自宅を訪問しアセスメントを行い、ケアマネジャーと連携して最適なサービス計画を作成します。"
              }
            },
            {
              "@type": "Question",
              "name": "介護保険は利用できますか？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "はい、介護保険の指定事業所です（介護指定番号: 2372902144）。介護保険を利用してサービスを受けることができます。"
              }
            }
          ]
        })}
      </script>
    </div>
  );
}
